import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSignIn } from 'react-auth-kit';
import { useUpdateAtom } from 'jotai/utils';
import * as Yup from 'yup';

import { Button, Input, Logo, Paper } from 'src/components';
import PasswordInput from 'src/components/PasswordInput';
import InvalidCode from 'src/pages/Onboarding/components/InvalidCode';
import Loading from 'src/pages/Onboarding/components/Loading';
import {
  attachDemoToAccount,
  completeDemoSignup,
  DemoSignupLoginResponse,
  DemoSignupValidateResponse,
  validateDemoSignup,
} from 'src/api/functions/signupApi';
import useForm from 'src/hooks/useForm';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';

import styles from './DemoSignup.module.scss';
import baseStyles from 'src/pages/Onboarding/styles.module.scss';

type ValidateState =
  | { phase: 'loading' }
  | { phase: 'invalid'; reason?: string }
  | { phase: 'already_claimed' }
  | { phase: 'ready'; data: DemoSignupValidateResponse };

// Local copy of the password policy (matches the server-side rules in
// UserManager._validate_password_policy). Identical to the live rules in
// `PasswordInput` so we don't have to depend on a global Yup schema.
const passwordYup = Yup.string()
  .required('Password is required')
  .min(8, 'At least 8 characters')
  .matches(/[A-Z]/, 'One uppercase letter')
  .matches(/[a-z]/, 'One lowercase letter')
  .matches(/\d/, 'One number')
  .matches(/[!@#$%^&*(),.?":{}|<>]/, 'One special character');

const NewUserSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  newPassword: passwordYup,
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .equals([Yup.ref('newPassword')], 'Passwords do not match'),
});

function isErrorResult(r: any): r is { status: number; error: any } {
  return r && typeof r === 'object' && 'status' in r && 'error' in r;
}

function errorCode(r: any): string | null {
  if (!isErrorResult(r)) return null;
  try {
    const err = typeof r.error === 'string' ? JSON.parse(r.error) : r.error;
    return err?.error?.code || null;
  } catch {
    return null;
  }
}

function errorMessage(r: any): string {
  if (!isErrorResult(r)) return 'Something went wrong, please try again.';
  try {
    const err = typeof r.error === 'string' ? JSON.parse(r.error) : r.error;
    return err?.error?.message || 'Something went wrong, please try again.';
  } catch {
    return 'Something went wrong, please try again.';
  }
}

const DemoSignup = () => {
  const location = useLocation();
  const history = useHistory();
  const signIn = useSignIn();
  // Used to seed `addonsSettingsAtom` optimistically from the
  // `ai_assistant_available` flag on the login envelope. Without this seed
  // the dashboard / nav / chat sidebar consumers all flash "AI Assistant
  // not available" on first paint while /api/v1/settings (which is what
  // populates the atom under normal flows) is in flight. The flag mirrors
  // `ai_settings.enabled` on the new account's group, which is set
  // synchronously by `bootstrap_demo_plan_account` before the response is
  // built — so it's safe to optimistically render with it.
  const updateAddonsSettings = useUpdateAtom(addonsSettingsAtom);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const demoRunId = (params.get('demo_run_id') || '').trim();
  const claimToken = (params.get('t') || '').trim();

  const [validateState, setValidateState] = useState<ValidateState>({ phase: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { form, handleChange, formErrors, setFieldError, clearFormErrors } = useForm({
    firstName: '',
    lastName: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    let cancelled = false;
    if (!demoRunId || !claimToken) {
      setValidateState({ phase: 'invalid' });
      return;
    }
    (async () => {
      const resp = await validateDemoSignup({
        demo_run_id: demoRunId,
        claim_token: claimToken,
      });
      if (cancelled) return;
      if (isErrorResult(resp)) {
        const code = errorCode(resp);
        if (code === 'ALREADY_CLAIMED') {
          setValidateState({ phase: 'already_claimed' });
        } else {
          setValidateState({ phase: 'invalid', reason: code || undefined });
        }
        return;
      }
      const data = resp as DemoSignupValidateResponse;
      setValidateState({ phase: 'ready', data });
      handleChange('firstName', data.suggested_first_name || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [demoRunId, claimToken]);

  const onLoginSuccess = (resp: DemoSignupLoginResponse) => {
    const ok = signIn({
      token: resp.jwt,
      expiresIn: 1440,
      tokenType: 'Bearer',
      authState: {
        isLoggedIn: true,
        accessId: resp.accessID,
        uploadkey: resp.uploadkey,
        role: resp.role_name,
        roleId: resp.role_id,
        permissions: resp.permissions,
        notifications: resp.notifications,
        preferences: resp.preferences,
        username: resp.username,
        userEmail: resp.user_email,
        userId: resp.user_id,
      },
    });
    if (!ok) {
      setSubmitError('Sign-in failed; please try again.');
      return;
    }
    if (resp.ai_assistant_available) {
      updateAddonsSettings({
        type: ACTION_TYPES.UPDATE_STATE,
        payload: { AI_ASSISTANT: { added: true, enabled: true } },
      });
    }
    history.push('/onboarding', {
      fromDemoClaim: true,
      demoClaim: resp.demo_claim,
      profileSnapshot: {
        email: validateState.phase === 'ready' ? validateState.data.email : resp.user_email,
        firstName: form.firstName,
        lastName: form.lastName,
        newPassword: form.newPassword,
      },
      orgName: resp.org_name,
      industry: resp.industry,
      aiAssistantAvailable: resp.ai_assistant_available,
    });
  };

  const submitNewUser = async (e: SyntheticEvent) => {
    e.preventDefault();
    clearFormErrors();
    setSubmitError(null);
    try {
      NewUserSchema.validateSync(form, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((ie) => ie.path && setFieldError(ie.path, ie.message));
      }
      return;
    }
    setSubmitting(true);
    const resp = await completeDemoSignup({
      demo_run_id: demoRunId,
      claim_token: claimToken,
      first_name: form.firstName,
      last_name: form.lastName,
      password: form.newPassword,
    });
    setSubmitting(false);
    if (isErrorResult(resp)) {
      const code = errorCode(resp);
      if (code === 'EMAIL_REGISTERED') {
        // Race: user got created between validate() and complete(). Swap to attach.
        setValidateState({
          phase: 'ready',
          data: {
            ...(validateState.phase === 'ready' ? validateState.data : ({} as any)),
            account_exists: true,
          },
        });
        setSubmitError('You already have an account - sign in to attach this share.');
        return;
      }
      if (code === 'ALREADY_CLAIMED') {
        setValidateState({ phase: 'already_claimed' });
        return;
      }
      if (code && code.startsWith('WEAK_PASSWORD')) {
        setFieldError('newPassword', errorMessage(resp));
        return;
      }
      setSubmitError(errorMessage(resp));
      return;
    }
    onLoginSuccess(resp as DemoSignupLoginResponse);
  };

  const submitAttach = async (e: SyntheticEvent) => {
    e.preventDefault();
    clearFormErrors();
    setSubmitError(null);
    // For the attach path the visitor already owns an existing account; we
    // don't re-validate against the live policy (their password may pre-date
    // the new rules). Just require it to be non-empty.
    if (!form.newPassword) {
      setFieldError('newPassword', 'Password is required');
      return;
    }
    setSubmitting(true);
    const resp = await attachDemoToAccount({
      demo_run_id: demoRunId,
      claim_token: claimToken,
      password: form.newPassword,
    });
    setSubmitting(false);
    if (isErrorResult(resp)) {
      const code = errorCode(resp);
      if (code === 'INVALID_CREDENTIALS') {
        setFieldError('newPassword', 'Invalid email or password');
        return;
      }
      if (code === 'ALREADY_CLAIMED') {
        setValidateState({ phase: 'already_claimed' });
        return;
      }
      if (code === 'ACCOUNT_DISABLED') {
        setSubmitError('Account is disabled or suspended. Please contact support.');
        return;
      }
      setSubmitError(errorMessage(resp));
      return;
    }
    onLoginSuccess(resp as DemoSignupLoginResponse);
  };

  if (validateState.phase === 'loading') {
    return <Loading />;
  }

  if (validateState.phase === 'invalid') {
    return <InvalidCode />;
  }

  if (validateState.phase === 'already_claimed') {
    return (
      <main className={baseStyles.onboarding}>
        <header>
          <Logo />
        </header>
        <section className={baseStyles.container}>
          <Paper className={baseStyles.content}>
            <h1>This demo share has already been claimed.</h1>
            <p>If you've signed up already, sign in to access it.</p>
            <Button onClick={() => history.push('/login')}>Go to sign in</Button>
          </Paper>
        </section>
      </main>
    );
  }

  const { email, account_exists } = validateState.data;

  return (
    <main className={baseStyles.onboarding}>
      <header>
        <Logo />
      </header>

      <section className={baseStyles.container}>
        <Paper className={baseStyles.content}>
          {account_exists ? (
            <>
              <h1>
                Welcome back, <span>{email}</span>
              </h1>
              <p>Sign in to attach this demo share to your account.</p>

              <form className={styles.form} onSubmit={submitAttach}>
                <Input
                  label="Email"
                  variant="secondary"
                  value={email}
                  onChange={() => {}}
                  disabled
                />
                <Input
                  type="password"
                  label="Password"
                  variant="secondary"
                  value={form.newPassword}
                  onChange={(v) => handleChange('newPassword', v)}
                  error={formErrors.newPassword}
                />
                {submitError && <p className={styles.error}>{submitError}</p>}
                <Button type="submit" loading={submitting}>
                  Sign in and attach
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1>
                Welcome to <span>FileSpin</span>
              </h1>
              <p>Finish creating your account to keep the demo share we just built for you.</p>

              <form className={styles.form} onSubmit={submitNewUser}>
                <Input
                  label="Email"
                  variant="secondary"
                  value={email}
                  onChange={() => {}}
                  disabled
                />
                <div className={styles.nameRow}>
                  <Input
                    label="First Name"
                    variant="secondary"
                    value={form.firstName}
                    onChange={(v) => handleChange('firstName', v)}
                    error={formErrors.firstName}
                  />
                  <Input
                    label="Last Name"
                    variant="secondary"
                    value={form.lastName}
                    onChange={(v) => handleChange('lastName', v)}
                    error={formErrors.lastName}
                  />
                </div>
                <PasswordInput
                  value={form.newPassword}
                  onChange={(v) => handleChange('newPassword', v)}
                  error={formErrors.newPassword}
                />
                <Input
                  type="password"
                  label="Confirm Password"
                  variant="secondary"
                  value={form.confirmPassword}
                  onChange={(v) => handleChange('confirmPassword', v)}
                  error={formErrors.confirmPassword}
                />
                {submitError && <p className={styles.error}>{submitError}</p>}
                <Button type="submit" loading={submitting}>
                  Create account
                </Button>
              </form>
            </>
          )}
        </Paper>
      </section>
    </main>
  );
};

export default DemoSignup;
